// Screen geometry for flat-top hexes. World units: one unit = hex circumradius
// (center to corner), so a hex is 2 units wide and √3 units tall.
// Floats are fine here: rendering never feeds back into the simulation.
// Conventions match src/sim/hex.ts (odd-q offsets, directions clockwise from N).
import type { Hex } from '../sim/types';

export const SQRT3 = Math.sqrt(3);

export type Point = { x: number; y: number };
export type Bounds = { minX: number; minY: number; maxX: number; maxY: number };

/** Axial step per direction 0..5 (N, NE, SE, S, SW, NW) — same table as sim/hex.ts. */
export const DIRS: readonly Hex[] = [
  { q: 0, r: -1 },
  { q: 1, r: -1 },
  { q: 1, r: 0 },
  { q: 0, r: 1 },
  { q: -1, r: 1 },
  { q: -1, r: 0 },
];

/** Corner i sits at angle 60°·i (screen y down). Side i runs corner i → i+1 and faces direction SIDE_DIR[i]. */
export const SIDE_DIR = [2, 3, 4, 5, 0, 1] as const;
/** Inverse: which side faces direction d. */
export const DIR_SIDE = [4, 5, 0, 1, 2, 3] as const;

const CORNER_DX = [1, 0.5, -0.5, -1, -0.5, 0.5];
const CORNER_DY = [0, 1, 1, 0, -1, -1].map((k) => (k * SQRT3) / 2);
// Integer lattice for corners: x = X/2, y = Y·√3/2. Lets us match shared corners exactly.
const CORNER_IX = [2, 1, -1, -2, -1, 1];
const CORNER_IY = [0, 1, 1, 0, -1, -1];

export function axialToOffset(h: Hex): [number, number] {
  return [h.q, h.r + (h.q - (h.q & 1)) / 2];
}

export function offsetToAxial(col: number, row: number): Hex {
  return { q: col, r: row - (col - (col & 1)) / 2 };
}

export function centerOfOffset(col: number, row: number): Point {
  return { x: 1.5 * col, y: SQRT3 * (row + 0.5 * (col & 1)) };
}

export function centerOf(h: Hex): Point {
  return { x: 1.5 * h.q, y: SQRT3 * (h.r + h.q / 2) };
}

export function cornerOfOffset(col: number, row: number, i: number): Point {
  const c = centerOfOffset(col, row);
  return { x: c.x + (CORNER_DX[i] as number), y: c.y + (CORNER_DY[i] as number) };
}

/** Exact integer key for corner i of hex (col,row); equal for every hex sharing that corner. */
export function cornerKey(col: number, row: number, i: number): string {
  const X = 3 * col + (CORNER_IX[i] as number);
  const Y = 2 * row + (col & 1) + (CORNER_IY[i] as number);
  return `${X},${Y}`;
}

export function cornerFromKey(key: string): Point {
  const [X, Y] = key.split(',').map(Number) as [number, number];
  return { x: X / 2, y: (Y * SQRT3) / 2 };
}

/** Neighbor of (col,row) in direction d, in offset coordinates. */
export function neighborOffset(col: number, row: number, d: number): [number, number] {
  const h = offsetToAxial(col, row);
  const s = DIRS[d] as Hex;
  return axialToOffset({ q: h.q + s.q, r: h.r + s.r });
}

/** Direction from a to adjacent b, or -1. */
export function dirBetween(a: Hex, b: Hex): number {
  const dq = b.q - a.q;
  const dr = b.r - a.r;
  return DIRS.findIndex((d) => d.q === dq && d.r === dr);
}

/** World point → the hex containing it (axial). */
export function hexAtPoint(p: Point): Hex {
  const qf = ((2 / 3) * p.x) / 1;
  const rf = (-1 / 3) * p.x + (SQRT3 / 3) * p.y;
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q: q + 0, r: r + 0 }; // + 0 turns -0 into 0
}

/** World-space bounding box of a width×height odd-q map. */
export function mapBounds(width: number, height: number): Bounds {
  return {
    minX: -1,
    minY: -SQRT3 / 2,
    maxX: 1.5 * (width - 1) + 1,
    maxY: SQRT3 * (height - 1) + SQRT3 / 2 + (width > 1 ? SQRT3 / 2 : 0),
  };
}

/** Deterministic 0..1 hash of integers (for stipple and building placement). */
export function hash01(a: number, b: number, c = 0): number {
  let h = Math.imul(a | 0, 374761393) ^ Math.imul(b | 0, 668265263) ^ Math.imul(c | 0, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
